import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormArray, FormControl, ReactiveFormsModule} from "@angular/forms";
import {NgClass} from "@angular/common";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MalsakId, MalsakModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatButtonModule} from "@angular/material/button";
import {filtrerBortNullInnslag} from "felles-ng2/støttefunksjoner/lister";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {TranslateModule} from "@ngx-translate/core";
import {HtmlFormatertKomponent} from "web-ng2/delt/komponenter/html-formatert/html-formatert";

const debug = initDebugger('MøteOpprettInkluderteSakerKontroller');

type SkjemaType = ReturnType<typeof MøteOpprettInkluderteSakerKontroller.opprettSkjema>;
type SkjemaVerdier = SkjemaType['value'];

@Component({
    selector: 'moete-opprett-inkluderte-saker',
    templateUrl: './møte-opprett-inkluderte-saker.html',
    styleUrls: ['./møte-opprett-inkluderte-saker.scss'],
    imports: [
        NgClass,
        ReactiveFormsModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        TipsKomponent,
        TranslateModule,
        HtmlFormatertKomponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteOpprettInkluderteSakerKontroller {

    private _skjema!: SkjemaType;
    private _malsaker!: MalsakModell[];

    sakmodeller: SakModell[] = [];

    static opprettSkjema() {
        return new FormArray<FormControl<boolean>>([])
    }

    /** Returnerer liste med MalsakId til alle saker i angitt `malsaker` som ar avhuket angitt `skjemaverdier`.
     * Forventer at angitt skjemaverdier er hentet fra skjema opprettet med {@link MøteOpprettInkluderteSakerKontroller#opprettSkjema} */
    static somMalsakIder(skjemaverdier: SkjemaVerdier, malsaker: MalsakModell[]): MalsakId[] {
        return malsaker
          .map((sak, indeks) => skjemaverdier[indeks] ? sak.id : null)
          .filter(filtrerBortNullInnslag()) as MalsakId[]
    }

    /** Returnerer antall saker valgt.
     * Forventer at angitt skjemaverdier er hentet fra skjema opprettet med {@link MøteOpprettInkluderteSakerKontroller#opprettSkjema} */
    static antallSakerValgt(skjemaverdier: SkjemaVerdier): number {
        return skjemaverdier.filter(erValgt => erValgt).length
    }

    constructor() {
    }

    @Input({required: true}) set skjema(skjema: SkjemaType) {
        this._skjema = skjema;
        this.initSakmodeller();
    }

    get skjema() {
        return this._skjema;
    }

    /** setter malsakene bruker skal kunne velge blant. */
    @Input({required: true}) set malsaker(malsaker: MalsakModell[]) {
        this._malsaker = malsaker;
        this.initSakmodeller();
    }

    get malsaker() {
        return this._malsaker;
    }

    antallValgt() {
        return MøteOpprettInkluderteSakerKontroller.antallSakerValgt(this.skjema.value);
    }

    alleValgt() {
        return this.skjema.value.every(erValgt => erValgt);
    }

    ingenValgt() {
        return this.skjema.value.every(erValgt => !erValgt);
    }

    endreAlle() {
        const erValgt = !this.alleValgt();
        this.skjema.controls.forEach(kontroll => kontroll.setValue(erValgt));
    }

    // private hjelpemetoder

    private initSakmodeller() {
        if (erNil(this.skjema) || erNil(this.malsaker)) return; //alle @Input må være satt

        this.skjema.clear({emitEvent: false});
        this.sakmodeller = this.malsaker
          .map(sak => {
              const kontroller = new FormControl(sak.standardIMoete, {nonNullable: true});
              this.skjema.push(kontroller);
              return {
                  sak: sak,
                  kontroller: kontroller,
                  erEkspandert: false
              };
          });
    }

}

interface SakModell {
    sak: MalsakModell;
    kontroller: FormControl<boolean>;
    erEkspandert: boolean;
}
